/**
 * Pure helpers for the vector attribute table: centroids, column order,
 * and row normalisation. Kept out of the TSX so the ToolCard and tests
 * share one source of truth.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

const ORIGIN_SHIFT = 20037508.342789244;
const GEOMETRY_KEYS = new Set(['geometry', 'Geometry', 'the_geom', 'shape']);
const COORD_KEYS = new Set(['X', 'Y', 'x', 'y']);
const ID_PRIORITY = [
  'OBJECTID_1',
  'OBJECTID',
  'objectid',
  'FID',
  'fid',
  'gid',
  'osm_id',
  'id',
  'ID',
] as const;

export interface LonLatPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Centroid of a GeoJSON geometry. Points return themselves; lines and
 * polygons use the average of the outer ring. Values already in lon/lat
 * stay as-is; Web Mercator metres are converted to lon/lat.
 */
export function centroidFromGeometry(geometry: unknown): LonLatPoint | null {
  const coords = coordinatesOf(geometry);
  if (!coords) return null;
  const positions = flattenPositions(coords);
  if (positions.length === 0) return null;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of positions) {
    sx += x;
    sy += y;
  }
  return toLonLat(sx / positions.length, sy / positions.length);
}

export function withCoordinateColumns(
  feature: VectorExportFeature,
): VectorExportFeature {
  const existing = feature.properties ?? {};
  const hasCoords =
    isFiniteNumber(existing.X) && isFiniteNumber(existing.Y);
  if (hasCoords) return feature;
  const centroid = centroidFromGeometry(feature.geometry);
  if (!centroid) return feature;
  return {
    ...feature,
    properties: {
      ...existing,
      X: roundCoord(centroid.x),
      Y: roundCoord(centroid.y),
    },
  };
}

/**
 * Column order matches a typical GIS attribute table: identifier fields
 * first, then X/Y, then the remaining properties in first-seen order
 * (shapefile field order), not alphabetical.
 */
export function attributeColumnsFromFeatures(
  features: ReadonlyArray<VectorExportFeature>,
): string[] {
  const seen = new Set<string>();
  const rest: string[] = [];
  for (const feature of features) {
    for (const key of Object.keys(feature.properties ?? {})) {
      if (seen.has(key) || GEOMETRY_KEYS.has(key) || COORD_KEYS.has(key)) continue;
      seen.add(key);
      rest.push(key);
    }
  }
  const ids = ID_PRIORITY.filter((key) => seen.has(key));
  const leftover = rest.filter((key) => !ids.includes(key as (typeof ID_PRIORITY)[number]));
  const hasCoords = features.some((feature) => {
    const props = feature.properties ?? {};
    return isFiniteNumber(props.X) || isFiniteNumber(props.Y);
  });
  return hasCoords ? [...ids, 'X', 'Y', ...leftover] : [...ids, ...leftover];
}

const OBJECTID_KEYS = ['OBJECTID_1', 'OBJECTID', 'objectid'] as const;

/** Sort rows by OBJECTID / OBJECTID_1 so the table matches GIS field order. */
export function sortAttributeRowsByObjectId(
  features: ReadonlyArray<VectorExportFeature>,
): VectorExportFeature[] {
  return [...features].sort((left, right) => {
    const a = objectIdValue(left);
    const b = objectIdValue(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  });
}

function objectIdValue(feature: VectorExportFeature): number | null {
  const props = feature.properties ?? {};
  for (const key of OBJECTID_KEYS) {
    const raw = props[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().length > 0) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  if (typeof feature.id === 'number' && Number.isFinite(feature.id)) return feature.id;
  if (typeof feature.id === 'string' && feature.id.trim().length > 0) {
    const parsed = Number(feature.id);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function dedupeAttributeRows(
  features: ReadonlyArray<VectorExportFeature>,
): VectorExportFeature[] {
  const out: VectorExportFeature[] = [];
  const seen = new Set<string>();
  for (const feature of features) {
    const key = rowIdentity(feature);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(feature);
  }
  return out;
}

export function featuresFromGeoJsonPayload(data: unknown): VectorExportFeature[] {
  const collection = asFeatureCollection(data);
  if (!collection) return [];
  return collection.map((entry, index) => {
    const record = entry as Record<string, unknown>;
    const properties =
      record.properties && typeof record.properties === 'object' && !Array.isArray(record.properties)
        ? (record.properties as Record<string, unknown>)
        : {};
    const id = record.id ?? properties.OBJECTID_1 ?? properties.OBJECTID ?? properties.id ?? index;
    return withCoordinateColumns({
      id: typeof id === 'string' || typeof id === 'number' ? id : index,
      properties,
      geometry: record.geometry,
    });
  });
}

function asFeatureCollection(data: unknown): unknown[] | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (record.type === 'FeatureCollection' && Array.isArray(record.features)) return record.features;
  if (record.type === 'Feature') return [record];
  if (Array.isArray(record.features)) return record.features;
  return null;
}

function rowIdentity(feature: VectorExportFeature): string {
  if (feature.id !== undefined && feature.id !== null && feature.id !== '') {
    return `id:${String(feature.id)}`;
  }
  const props = feature.properties ?? {};
  for (const key of ID_PRIORITY) {
    const value = props[key];
    if (value !== undefined && value !== null && value !== '') return `${key}:${String(value)}`;
  }
  return `row:${JSON.stringify(props)}`;
}

function coordinatesOf(geometry: unknown): unknown {
  if (!geometry || typeof geometry !== 'object') return null;
  const record = geometry as { type?: unknown; coordinates?: unknown; getCoordinates?: () => unknown };
  if (typeof record.getCoordinates === 'function') return record.getCoordinates();
  if (record.coordinates !== undefined) return record.coordinates;
  return null;
}

function flattenPositions(value: unknown): ReadonlyArray<readonly [number, number]> {
  if (!Array.isArray(value) || value.length === 0) return [];
  if (typeof value[0] === 'number') {
    const x = value[0];
    const y = value[1];
    return typeof x === 'number' && typeof y === 'number' ? [[x, y]] : [];
  }
  const out: Array<readonly [number, number]> = [];
  for (const child of value) {
    out.push(...flattenPositions(child));
  }
  return out;
}

function toLonLat(x: number, y: number): LonLatPoint {
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return { x, y };
  const lon = (x / ORIGIN_SHIFT) * 180;
  const lat =
    (Math.atan(Math.sinh((y / ORIGIN_SHIFT) * Math.PI)) * 180) / Math.PI;
  return { x: lon, y: lat };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function roundCoord(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
